// @system — Roles, permissions and staff API client (server: api/@system/roles).
// Every call goes through the shared HTTP client (lib/@system/api.js): cookie
// session, CSRF header, ApiError on non-2xx — no raw fetch in feature code.
import { api } from '../../lib/@system/api.js'

/**
 * @param {Record<string, unknown>} [params]
 * @returns {string} `?a=b` query string; empty when nothing is set
 */
function qs(params = {}) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (entries.length === 0) return ''
  const search = new URLSearchParams()
  for (const [k, v] of entries) search.set(k, Array.isArray(v) ? v.join(',') : String(v))
  return `?${search.toString()}`
}

/**
 * @typedef {Object} Role
 * @property {string} slug
 * @property {string} name
 * @property {string|null} description
 * @property {boolean} is_system
 * @property {string[]} permissions
 * @property {number} user_count
 */
/**
 * @typedef {Object} PermissionCatalogEntry
 * @property {string} key
 * @property {string} group
 * @property {string} description
 */
/**
 * @typedef {Object} StaffUser
 * @property {number} id
 * @property {string} email
 * @property {string|null} name
 * @property {string} role
 * @property {string} created_at
 * @property {string|null} last_seen_at
 */

/** Caller's role + sorted permission keys (`{ data: { role, permissions, source } }`). */
export const getMyPermissions = () => api.get('/permissions/me')
/**
 * `GET /admin/roles` body: every role, the permission catalogue and where the matrix was read from.
 * @typedef {{ data: Role[], catalog: PermissionCatalogEntry[], source: string }} RolesResponse
 */
/**
 * Pagination block of the admin list endpoints — `{ page, limit, total, totalPages }`
 * (not `Api.Pagination`, whose page count is `pages`).
 * @typedef {{ page: number, limit: number, total: number, totalPages: number }} AdminListPagination
 */
/** `GET /admin/staff` body. @typedef {{ data: StaffUser[], pagination?: AdminListPagination }} StaffListResponse */

/**
 * Every role with its permissions plus the permission catalogue (`platform.admin`).
 * @returns {Promise<RolesResponse>}
 */
export const adminGetRoles = () => api.get('/admin/roles')
/**
 * Replace a role's permission set (`staff.manage`). 409 for `owner` or when
 * dropping `staff.manage` from your own role; 400 for unknown keys.
 * @param {string} slug
 * @param {string[]} permissions
 */
export const adminUpdateRolePermissions = (slug, permissions) =>
  api.put(`/admin/roles/${encodeURIComponent(slug)}/permissions`, { permissions })
/**
 * Users holding an admin role (`staff.manage`).
 * @param {{ q?: string, page?: number, limit?: number }} [params]
 * @returns {Promise<StaffListResponse>}
 */
export const adminGetStaff = (params) => api.get(`/admin/staff${qs(params)}`)
/**
 * Any account by exact email — used to promote a member (`staff.manage`); 404 when unknown.
 * @param {string} email
 */
export const adminLookupStaff = (email) => api.get(`/admin/staff/lookup${qs({ email })}`)
/**
 * Assign a role (`staff.manage`). 403 own role / non-owner touching owner,
 * 409 last owner, 400 unknown role, 404 unknown user.
 * @param {number|string} id
 * @param {string} role
 */
export const adminSetStaffRole = (id, role) => api.patch(`/admin/staff/${encodeURIComponent(String(id))}/role`, { role })
